'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logger } from '@/lib/logger.client';

import Icon from './Icon';
import ProductFilterSection, { ProductFilterOption } from './ProductFilterSection';
import ProductOrderSection, { SortOption } from './ProductOrderSection';
import ProductPriceRangeSection from './ProductPriceRangeSection';

export type ProductFilterData = {
  categories: ProductFilterOption[];
  lines: ProductFilterOption[];
  sizes: ProductFilterOption[];
  colors: ProductFilterOption[];
  priceRange: {
    min: number;
    max: number;
  };
  sortOptions: SortOption[];
};

export type ActiveProductFilters = {
  categories: string[];
  lines: string[];
  sizes: string[];
  colors: string[];
  priceMin: number;
  priceMax: number;
  order: string;
};

export type ProductFilterProps = {
  filterData: ProductFilterData;
  activeFilters: ActiveProductFilters;
  onFiltersChange: (filters: ActiveProductFilters) => void;
  onClearFilters: () => void;
  className?: string;
  isMobileOpen?: boolean;
  onMobileToggle?: () => void;
};

// Sizes available per line. Used to restrict the sizes filter when lines are selected.
const SIZES_MAP: Record<string, string[]> = {
  COLEGIAL: ['02','O2', '03', '04', '06', '08', '10', '12', '14'],
  'JUNIOR NENA': ['02','O2', '03', '04', '06', '08', '10', '12', '14'],
  'JUNIOR VARON': ['02', 'O2','03', '04', '06', '08', '10', '12', '14'],
  'BEBE NENA': ['OM', 'OL', '12M', '18M', '02','O2',],
  'BABY SHOWER': ['OM', 'OL', '12M', '18M', '02','O2'],
  'BEBE VARON': ['OM', 'OL', '12M', '18M', '02','O2'],
};

const ProductFilter: React.FC<ProductFilterProps> = ({ filterData, activeFilters, onFiltersChange, onClearFilters, className = '', isMobileOpen = false, onMobileToggle }) => {
  const [localFilters, setLocalFilters] = useState<ActiveProductFilters>(activeFilters);
  const priceDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sizes to show depend on selected lines. When lines are active, restrict to
  // the union of sizes allowed for those lines (matched case-insensitively).
  // Falls back to all sizes from filterData when no lines are selected.
  const visibleSizes = useMemo(() => {
    if (localFilters.lines.length === 0) return filterData.sizes;
    const allowed = new Set<string>();
    for (const line of localFilters.lines) {
      const key = Object.keys(SIZES_MAP).find((k) => k.toUpperCase() === line.toUpperCase());
      if (key) SIZES_MAP[key].forEach((s) => allowed.add(s));
    }
    return allowed.size > 0
      ? filterData.sizes.filter((s) => allowed.has(s.value))
      : filterData.sizes;
  }, [localFilters.lines, filterData.sizes]);

  // Filter selected sizes to only include visible sizes
  const validSelectedSizes = useMemo(() => {
    const validSizeValues = new Set(visibleSizes.map((s) => s.value));
    return localFilters.sizes?.filter((size) => validSizeValues.has(size));
  }, [localFilters.sizes, visibleSizes]);

  // Update local filters when active filters change from parent
  useEffect(() => {
    setLocalFilters(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    logger.debug({ msg: 'ProductFilter filterData updated', filterData });
  }, [filterData]);

  // Update filters and apply immediately
  const updateFilters = useCallback(
    (newFilters: Partial<ActiveProductFilters>) => {
      const updated = { ...localFilters, ...newFilters };
      setLocalFilters(updated);
      onFiltersChange(updated);
    },
    [localFilters, onFiltersChange],
  );

  // Update price filters with debounce
  const updatePriceFilters = useCallback(
    (min: number, max: number) => {
      const updated = { ...localFilters, priceMin: min, priceMax: max };
      setLocalFilters(updated);

      // Clear existing timer
      if (priceDebounceTimerRef.current) {
        clearTimeout(priceDebounceTimerRef.current);
      }

      // Set new timer for debounced update
      priceDebounceTimerRef.current = setTimeout(() => {
        onFiltersChange(updated);
      }, 500); // 500ms debounce
    },
    [localFilters, onFiltersChange],
  );

  // Update lines and clear incompatible sizes
  const updateLinesFilter = useCallback(
    (lines: string[]) => {
      // If lines changed, we need to clear sizes that are no longer valid
      const updated = { ...localFilters, lines, sizes: [] };
      setLocalFilters(updated);
      onFiltersChange(updated);
    },
    [localFilters, onFiltersChange],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (priceDebounceTimerRef.current) {
        clearTimeout(priceDebounceTimerRef.current);
      }
    };
  }, []);

  // Count active filters
  const activeFilterCount =
    localFilters.categories.length +
    localFilters.lines.length +
    localFilters.sizes.length +
    localFilters.colors.length +
    (localFilters.order !== '' ? 1 : 0) +
    (localFilters.priceMin !== filterData.priceRange.min || localFilters.priceMax !== filterData.priceRange.max ? 1 : 0);

  const hasActiveProductFilters = activeFilterCount > 0;

  const handleClearFilters = useCallback(() => {
    const clearedFilters: ActiveProductFilters = {
      categories: [],
      lines: [],
      sizes: [],
      colors: [],
      priceMin: filterData.priceRange.min,
      priceMax: filterData.priceRange.max,
      order: '',
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
    if (isMobileOpen && onMobileToggle) {
      onMobileToggle();
    }
  }, [filterData.priceRange.min, filterData.priceRange.max, onClearFilters, isMobileOpen, onMobileToggle]);

  useEffect(() => {
    logger.debug({ msg: 'ProductFilter filterData updated', filterData });
  }, [filterData]);

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && <div className="fixed inset-0 bg-gray-900 bg-opacity-20 backdrop-blur-sm z-40 lg:hidden" onClick={onMobileToggle} />}

      {/* Filter Sidebar */}
      <aside
        className={`
        ${className}
        lg:relative lg:translate-x-0 lg:bg-transparent lg:shadow-none lg:z-auto
        md:p-10 p-1
        ${isMobileOpen ? 'fixed top-0 left-0 z-50 translate-x-0' : 'fixed top-0 left-0 z-50 -translate-x-full'}
        w-80 h-full bg-white shadow-xl transition-transform duration-300 ease-in-out
        lg:w-full lg:h-auto lg:max-w-xs
      `}
      >
        {/* Mobile Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
          <div className="flex items-center space-x-2">
            <Icon name="filter" size={20} />
            <h2 className="text-lg font-semibold">Filtros</h2>
            {hasActiveProductFilters && <span className="bg-secondary-700 text-primary-800 flex items-center justify-center font-bold text-xs w-7 h-7 rounded-full">{activeFilterCount}</span>}
          </div>
          <button onClick={onMobileToggle} className="p-1 text-gray-400 hover:text-gray-600">
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="filter" size={18} />
            <h2 className="text-lg font-semibold">Filtros</h2>
            {hasActiveProductFilters && <span className="bg-secondary-700 text-primary-800 flex items-center justify-center font-bold text-xs w-7 h-7 rounded-full">{activeFilterCount}</span>}
          </div>
        </div>

        {/* Filter Content */}
        <div className="p-4 lg:p-0 overflow-y-auto h-full lg:h-auto">
          <div className="space-y-6">
            {/* Clear Filters Button - Mobile only now */}
            {hasActiveProductFilters && (
              <button onClick={handleClearFilters} className="lg:hidden w-full py-2 px-4 font-medium bg-secondary-500 hover:bg-secondary-600 rounded-md transition-colors cursor-pointer">
                Limpiar filtros ({activeFilterCount})
              </button>
            )}

            {/* Lines Filter */}
            {filterData.lines.length > 0 && (
              <ProductFilterSection title="Líneas" options={filterData.lines} selectedValues={localFilters.lines} onChange={(values) => updateLinesFilter(values)} type="checkbox" />
            )}

            {/* Categories Filter */}
            {filterData.categories.length > 0 && (
              <ProductFilterSection
                title="Categorías"
                options={filterData.categories}
                selectedValues={localFilters.categories}
                onChange={(values) => updateFilters({ categories: values })}
                type="checkbox"
              />
            )}

            {/* Sizes Filter */}
            {visibleSizes.length > 0 && (
              <ProductFilterSection
                title="Talles"
                options={visibleSizes}
                selectedValues={validSelectedSizes}
                onChange={(values) => updateFilters({ sizes: values })}
                type="checkbox"
              />
            )}

            {/* Colors Filter */}
            {filterData.colors.length > 0 && (
              <ProductFilterSection
                title="Colores"
                options={filterData.colors}
                selectedValues={localFilters.colors}
                onChange={(values) => updateFilters({ colors: values })}
                type="checkbox"
              />
            )}

            {/* Price Range Filter */}
            <ProductPriceRangeSection
              title="Rango de Precio"
              min={filterData.priceRange.min}
              max={filterData.priceRange.max}
              selectedMin={localFilters.priceMin}
              selectedMax={localFilters.priceMax}
              onChange={updatePriceFilters}
              currency="$"
            />

            {/* Sort Options */}
            {filterData.sortOptions.length > 0 && (
              <ProductOrderSection title="Ordenar por" options={filterData.sortOptions} selectedValue={localFilters.order} onChange={(value) => updateFilters({ order: value })} />
            )}
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden lg:block mt-6 pt-4 border-t border-secondary-600">
            {hasActiveProductFilters && (
              <button
                onClick={handleClearFilters}
                className=" w-full py-2 px-4 font-medium border border-secondary-600 bg-secondary-400 hover:bg-secondary-500 rounded-md transition-colors cursor-pointer"
              >
                Limpiar filtros ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Mobile Action Buttons */}
          <div className="lg:hidden mt-6 pb-4">
            {hasActiveProductFilters && (
              <button onClick={handleClearFilters} className="lg:hidden w-full py-2 px-4 font-medium bg-secondary-500 hover:bg-secondary-600 rounded-sm transition-colors cursor-pointer">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default ProductFilter;
